import { faSearch } from "@fortawesome/pro-regular-svg-icons";
import type { ChangeEvent, HTMLProps } from "react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

import { Skeleton, TextField, type TextFieldProps } from "@aviary";
import type { GroupName } from "@shared/components/Typeahead";
import { Typeahead, TypeaheadResultsContainer } from "@shared/components/Typeahead";
import { useTypeaheadSearchV2LazyQuery } from "@shared/data/queries";
import { useKeyboardNavigationList, useIsTouchscreen, useBreakpoints } from "@shared/hooks";
import { l } from "@shared/locales/i18n";
import type { Typeahead as TypeaheadType } from "@shared/types/graphqlGenerated";

import type { TypeaheadViewAll } from "../../Typeahead/shared/TypeaheadViewAll";

import * as styles from "./SearchInput.styles";

const loadingSkeleton = Array.from({ length: 5 }).map((_, i) => (
  <div key={i} css={styles.skeletonLineItem}>
    <Skeleton
      type="typography"
      typographySize="h2"
      css={i === 0 ? styles.skeletonButton : styles.skeletonSubButton}
      margin="topAndBottom"
    />
  </div>
));

interface Props extends HTMLProps<HTMLDivElement> {
  onSearchContentClose: () => void;
  onSearchContentOpen: () => void;
  onSearchValueChange: (value: string) => void;
  onQuerySearch: () => void;
  onClearQuerySearch: () => void;
  onTypeaheadSelection: (searchFilter: Pick<TypeaheadType, "id" | "name">) => void;
  searchValue: string;
  showSearchContent: boolean;
  inputSize?: TextFieldProps["size"];
  placeholderOverride?: string;
  goToProductDetails: (productId: string, productSlug?: string) => void;
  logTypeaheadAnalytics?: (entityName: string, entityType: string) => void;
}

const SearchInput = ({
  onSearchContentClose,
  onSearchContentOpen,
  onSearchValueChange,
  onQuerySearch,
  onClearQuerySearch,
  onTypeaheadSelection,
  searchValue,
  showSearchContent,
  inputSize,
  placeholderOverride,
  goToProductDetails,
  logTypeaheadAnalytics,
  ...rest
}: Props) => {
  const { t } = useTranslation("common");

  const { phoneLarge } = useBreakpoints();

  const containerRef = useRef(null);

  const [execute, { loading, data }] = useTypeaheadSearchV2LazyQuery();

  const isTouchscreen = useIsTouchscreen();

  const typeaheadViewAll: TypeaheadViewAll[] = [
    {
      id: "ViewAllProduct",
      groupName: "Product",
      onTrigger: onQuerySearch,
    },
  ];

  const typeaheadResults = data?.viewer?.typeaheadSearchV2 ?? [];
  let keyboardResultSet: Typeahead[] = [
    {
      id: "searchBarId",
      name: "searchBar",
      entityType: "searchBar",
    },
  ];

  const resultsViewAll: TypeaheadViewAll[] = [];

  typeaheadResults.forEach(group => {
    keyboardResultSet = keyboardResultSet.concat(group?.data);

    const groupViewAll = typeaheadViewAll.find(viewAll => viewAll.groupName === group?.entityType);
    resultsViewAll.push(groupViewAll);

    if (groupViewAll) {
      keyboardResultSet.push({
        id: groupViewAll.id,
        name: groupViewAll.groupName,
        entityType: groupViewAll.groupName,
      });
    }
  });

  const handleEnter = () => {
    if (activeKeyboardIndex === 0) {
      return onQuerySearch();
    }

    const isOnViewAll = typeaheadViewAll.find(viewAll => viewAll.id === activeKeyboardRow?.id);
    if (isOnViewAll) {
      return isOnViewAll.onTrigger();
    }

    onTypeaheadResultSelectedV2(activeKeyboardRow);
  };

  const {
    itemSelectedIndex: activeKeyboardIndex,
    onKeyDown,
    onFocus,
  } = useKeyboardNavigationList(
    keyboardResultSet.length,
    containerRef,
    onSearchContentClose,
    handleEnter,
    null,
    onSearchContentClose
  );

  const activeKeyboardRow = keyboardResultSet[activeKeyboardIndex];

  const debouncedSearch = debouncedSearchValue => {
    execute({
      variables: {
        query: debouncedSearchValue,
        filters: {
          list: ["PRODUCTS", "INGREDIENTS", "BRANDS", "SUPPLEMENT_TYPES", "CONDITIONS"],
        },
      },
    });
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    if (value?.length < 256) {
      onSearchValueChange(value);
    }
  };

  const onClick = () => {
    if (!showSearchContent) {
      onSearchContentOpen();
    } else {
      onSearchContentClose();
    }
  };

  const onTypeaheadResultSelectedV2 = (
    typeaheadSearch: Pick<
      TypeaheadType,
      "id" | "name" | "entityType" | "entityTypeId" | "productSlug"
    >
  ) => {
    logTypeaheadAnalytics?.(typeaheadSearch?.name, typeaheadSearch?.entityType);
    if (typeaheadSearch?.entityType === "Product") {
      goToProductDetails(typeaheadSearch?.id, typeaheadSearch?.productSlug);
    } else {
      onTypeaheadSelection(typeaheadSearch);
    }
  };

  const groupNameTranslation = (name: GroupName, count: number): GroupName => {
    switch (name) {
      case "Product":
        return t(l.sharedCatalog.typeahead.groupName.Product, {
          count,
        });
      case "Ingredient":
        return t(l.sharedCatalog.typeahead.groupName.Ingredient, {
          count,
        });
      case "Brand":
        return t(l.sharedCatalog.typeahead.groupName.Brand, {
          count,
        });
      case "SupplementType":
        return t(l.sharedCatalog.typeahead.groupName.SupplementType, {
          count,
        });
      case "Condition":
        return t(l.sharedCatalog.typeahead.groupName.Condition, {
          count,
        });
    }
  };

  const getGroupNames = (): GroupName[] =>
    typeaheadResults.map(group => {
      const name = group?.entityType;
      const count = group?.data?.length;

      return groupNameTranslation(name, count);
    });

  const renderContent = () => {
    if (!showSearchContent) {
      return null;
    }

    if (searchValue?.length > 0) {
      if (loading) {
        return <TypeaheadResultsContainer>{loadingSkeleton}</TypeaheadResultsContainer>;
      }

      return (
        <Typeahead
          query={searchValue}
          results={typeaheadResults}
          highlightRowId={activeKeyboardRow?.id}
          onResultClick={onTypeaheadResultSelectedV2}
          viewAllGroups={resultsViewAll}
          groupNames={getGroupNames()}
        />
      );
    }
  };

  const getPlaceholder = () => {
    if (placeholderOverride) {
      return placeholderOverride;
    }

    if (phoneLarge.greaterThan) {
      return t(l.sharedCommon.SearchAll);
    }

    return t(l.sharedCommon.SearchProducts);
  };

  return (
    <div css={styles.catalogSearch} ref={containerRef} {...rest}>
      <TextField
        size={inputSize}
        isFocused={!isTouchscreen}
        placeholder={getPlaceholder()}
        value={searchValue}
        onFocus={onFocus}
        onChange={handleSearchChange}
        isDebounced
        onDebouncedChange={debouncedSearch}
        hasClearButton={searchValue !== ""}
        onClear={onClearQuerySearch}
        aria-label={getPlaceholder()}
        onKeyDown={onKeyDown}
        onMouseDown={onClick}
        data-e2e="search-products"
        isFullwidth
        inputIcon={faSearch}
      />
      {renderContent()}
    </div>
  );
};

SearchInput.defaultProps = {
  inputSize: "large",
};

export { SearchInput };
